import React, { createContext, useState, useContext, useCallback, useMemo, ReactNode } from 'react';
import HighlightDebugPanel from '@/components/debug/HighlightDebugPanel';
import { PoemHighlight } from '@/types/highlights';

interface HighlightDebugContextType {
  isDebugEnabled: boolean;
  enableDebug: () => void;
  disableDebug: () => void;
  toggleDebug: () => void;
  toggleDebugPanel: () => void;
  isDebugPanelVisible: boolean;
  captureHighlightData: (text: string[], highlights: PoemHighlight[]) => void;
}

const HighlightDebugContext = createContext<HighlightDebugContextType>({
  isDebugEnabled: false,
  enableDebug: () => {},
  disableDebug: () => {},
  toggleDebug: () => {},
  toggleDebugPanel: () => {},
  isDebugPanelVisible: false,
  captureHighlightData: () => {}
});

interface HighlightDebugProviderProps {
  children: ReactNode;
  isPoemAdmin?: boolean;
}

export const HighlightDebugProvider: React.FC<HighlightDebugProviderProps> = ({ 
  children,
  isPoemAdmin = false
}) => {
  const [isDebugEnabled, setIsDebugEnabled] = useState(false);
  const [isDebugPanelVisible, setIsDebugPanelVisible] = useState(false);
  const [poemText, setPoemText] = useState<string[]>([]);
  const [highlights, setHighlights] = useState<PoemHighlight[]>([]);

  // Enable debug mode
  const enableDebug = useCallback(() => {
    // Update the DEBUG_CONFIG in HighlightedLine component if it's imported
    if (typeof window !== 'undefined') {
      // @ts-ignore - accessing window property that might not exist in all environments
      if (window.DEBUG_CONFIG) {
        // @ts-ignore
        window.DEBUG_CONFIG.enabled = true;
      }
    }
    setIsDebugEnabled(true);
  }, []);

  // Disable debug mode
  const disableDebug = useCallback(() => {
    // Update the DEBUG_CONFIG in HighlightedLine component if it's imported
    if (typeof window !== 'undefined') {
      // @ts-ignore - accessing window property that might not exist in all environments
      if (window.DEBUG_CONFIG) {
        // @ts-ignore
        window.DEBUG_CONFIG.enabled = false;
      }
    }
    setIsDebugEnabled(false);
    setIsDebugPanelVisible(false);
  }, []);

  // Toggle debug mode
  const toggleDebug = useCallback(() => {
    if (isDebugEnabled) {
      disableDebug();
    } else {
      enableDebug();
    }
  }, [isDebugEnabled, enableDebug, disableDebug]);

  // Toggle debug panel visibility
  const toggleDebugPanel = useCallback(() => {
    // If enabling the panel, also make sure debug mode is enabled
    if (!isDebugPanelVisible && !isDebugEnabled) {
      enableDebug();
    }
    setIsDebugPanelVisible(prev => !prev);
  }, [isDebugPanelVisible, isDebugEnabled, enableDebug]);

  // Capture poem text and highlights when debug panel is visible
  const captureHighlightData = useCallback((text: string[], poemHighlights: PoemHighlight[]) => {
    setPoemText(text);
    setHighlights(poemHighlights);
  }, []);

  // Context value
  const value = useMemo(() => ({
    isDebugEnabled,
    enableDebug,
    disableDebug,
    toggleDebug,
    toggleDebugPanel,
    isDebugPanelVisible,
    captureHighlightData
  }), [
    isDebugEnabled, 
    enableDebug, 
    disableDebug, 
    toggleDebug, 
    toggleDebugPanel, 
    isDebugPanelVisible,
    captureHighlightData
  ]);

  return (
    <HighlightDebugContext.Provider value={value}>
      {children}
      
      {/* Only render the debug panel for admin users and when it's visible */}
      {isPoemAdmin && isDebugPanelVisible && (
        <HighlightDebugPanel 
          poemText={poemText}
          highlights={highlights}
          onClose={() => setIsDebugPanelVisible(false)}
        />
      )}
    </HighlightDebugContext.Provider>
  );
};

// Custom hook for using the highlight debug context
export const useHighlightDebug = () => {
  const context = useContext(HighlightDebugContext);
  if (context === undefined) {
    throw new Error('useHighlightDebug must be used within a HighlightDebugProvider');
  }
  return context;
};

// Public debug API for use in the browser console
export const setupHighlightDebugAPI = () => {
  if (typeof window !== 'undefined') {
    // @ts-ignore - Attaching to window
    window.highlightDebug = {
      enable: () => {
        // @ts-ignore
        if (window.DEBUG_CONFIG) window.DEBUG_CONFIG.enabled = true;
      },
      disable: () => {
        // @ts-ignore
        if (window.DEBUG_CONFIG) window.DEBUG_CONFIG.enabled = false;
      },
      toggle: () => {
        // @ts-ignore
        if (window.DEBUG_CONFIG) window.DEBUG_CONFIG.enabled = !window.DEBUG_CONFIG.enabled;
      },
      getState: () => {
        // @ts-ignore
        return window.DEBUG_CONFIG ? window.DEBUG_CONFIG.enabled : false;
      }
    };
  }
};

export default HighlightDebugContext; 